/* BildLokal – Shopify Storefront API Client
   Alle Produkt- und Warenkorb-Daten kommen live aus dem echten
   Shopify-Store. Kein lokaler Demo-Katalog mehr. */

const SHOPIFY_CART_ID_KEY = "bildlokal_shopify_cart_id";

class ShopifyConfigError extends Error {}

async function shopifyFetch(query, variables = {}) {
  if (!SHOPIFY_CONFIG.storefrontAccessToken || SHOPIFY_CONFIG.storefrontAccessToken.startsWith("PASTE_")) {
    throw new ShopifyConfigError(
      "Es ist noch kein Shopify Storefront-API-Token hinterlegt. Trage ihn in js/shopify-config.js ein."
    );
  }

  const url = `https://${SHOPIFY_CONFIG.domain}/api/${SHOPIFY_CONFIG.apiVersion}/graphql.json`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": SHOPIFY_CONFIG.storefrontAccessToken,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    throw new Error(`Shopify Storefront API antwortete mit Status ${res.status}`);
  }

  const json = await res.json();
  if (json.errors) {
    throw new Error(json.errors.map((e) => e.message).join(", "));
  }
  return json.data;
}

const PRODUCT_FIELDS = `
  id
  title
  handle
  description
  vendor
  productType
  featuredImage { url altText }
  images(first: 6) { edges { node { url altText } } }
  priceRange {
    minVariantPrice { amount currencyCode }
    maxVariantPrice { amount currencyCode }
  }
  options { name optionValues { name } }
  variants(first: 30) {
    edges {
      node {
        id
        title
        availableForSale
        price { amount currencyCode }
        selectedOptions { name value }
        image { url altText }
      }
    }
  }
`;

function normalizeProduct(node) {
  const images = node.images.edges.map((e) => e.node.url);
  return {
    id: node.id,
    handle: node.handle,
    title: node.title,
    description: node.description,
    creator: node.vendor || "BildLokal",
    category: node.productType || "Kunst",
    image: (node.featuredImage && node.featuredImage.url) || images[0] || "",
    images,
    minPrice: parseFloat(node.priceRange.minVariantPrice.amount),
    maxPrice: parseFloat(node.priceRange.maxVariantPrice.amount),
    currency: node.priceRange.minVariantPrice.currencyCode,
    options: node.options.map((o) => ({
      name: o.name,
      values: o.optionValues.map((v) => v.name),
    })),
    variants: node.variants.edges.map((e) => ({
      id: e.node.id,
      title: e.node.title,
      available: e.node.availableForSale,
      price: parseFloat(e.node.price.amount),
      currency: e.node.price.currencyCode,
      selectedOptions: e.node.selectedOptions,
      image: e.node.image ? e.node.image.url : null,
    })),
  };
}

async function fetchAllProducts() {
  const query = `
    query Products($first: Int!) {
      products(first: $first, sortKey: TITLE) {
        edges { node { ${PRODUCT_FIELDS} } }
      }
    }
  `;
  const data = await shopifyFetch(query, { first: 50 });
  return data.products.edges.map((e) => normalizeProduct(e.node));
}

async function fetchProductByHandle(handle) {
  const query = `
    query ProductByHandle($handle: String!) {
      product(handle: $handle) { ${PRODUCT_FIELDS} }
    }
  `;
  const data = await shopifyFetch(query, { handle });
  return data.product ? normalizeProduct(data.product) : null;
}

function findVariant(product, selection) {
  return product.variants.find((v) =>
    v.selectedOptions.every((o) => selection[o.name] === o.value)
  );
}

function formatMoney(amount, currency) {
  return new Intl.NumberFormat("de-CH", { style: "currency", currency: currency || "CHF" }).format(amount);
}

/* ---------- Cart (Shopify Storefront Cart API) ---------- */

const CART_FIELDS = `
  id
  checkoutUrl
  totalQuantity
  cost {
    subtotalAmount { amount currencyCode }
    totalAmount { amount currencyCode }
  }
  lines(first: 50) {
    edges {
      node {
        id
        quantity
        cost { totalAmount { amount currencyCode } }
        merchandise {
          ... on ProductVariant {
            id
            title
            image { url altText }
            product { title handle }
            selectedOptions { name value }
          }
        }
      }
    }
  }
`;

function normalizeCart(cart) {
  if (!cart) return null;
  return {
    id: cart.id,
    checkoutUrl: cart.checkoutUrl,
    totalQuantity: cart.totalQuantity,
    subtotal: parseFloat(cart.cost.subtotalAmount.amount),
    total: parseFloat(cart.cost.totalAmount.amount),
    currency: cart.cost.subtotalAmount.currencyCode,
    lines: cart.lines.edges.map((e) => ({
      id: e.node.id,
      quantity: e.node.quantity,
      lineTotal: parseFloat(e.node.cost.totalAmount.amount),
      currency: e.node.cost.totalAmount.currencyCode,
      variantId: e.node.merchandise.id,
      title: e.node.merchandise.product.title,
      handle: e.node.merchandise.product.handle,
      variantTitle: e.node.merchandise.title,
      image: e.node.merchandise.image ? e.node.merchandise.image.url : null,
      selectedOptions: e.node.merchandise.selectedOptions,
    })),
  };
}

async function shopifyCartCreate(lines = []) {
  const query = `
    mutation CartCreate($input: CartInput) {
      cartCreate(input: $input) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `;
  const data = await shopifyFetch(query, { input: { lines } });
  if (data.cartCreate.userErrors.length) {
    throw new Error(data.cartCreate.userErrors.map((e) => e.message).join(", "));
  }
  return data.cartCreate.cart;
}

async function getCartId() {
  return localStorage.getItem(SHOPIFY_CART_ID_KEY);
}

async function shopifyAddToCart(variantId, quantity) {
  let cartId = await getCartId();
  const line = { merchandiseId: variantId, quantity };

  if (!cartId) {
    const cart = await shopifyCartCreate([line]);
    localStorage.setItem(SHOPIFY_CART_ID_KEY, cart.id);
    return normalizeCart(cart);
  }

  const query = `
    mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `;
  const data = await shopifyFetch(query, { cartId, lines: [line] });

  if (data.cartLinesAdd.userErrors.length || !data.cartLinesAdd.cart) {
    // Cart existiert nicht mehr (z.B. abgelaufen) -> neuen Cart anlegen
    localStorage.removeItem(SHOPIFY_CART_ID_KEY);
    const cart = await shopifyCartCreate([line]);
    localStorage.setItem(SHOPIFY_CART_ID_KEY, cart.id);
    return normalizeCart(cart);
  }
  return normalizeCart(data.cartLinesAdd.cart);
}

async function shopifyGetCart() {
  const cartId = await getCartId();
  if (!cartId) return null;
  const query = `query CartGet($id: ID!) { cart(id: $id) { ${CART_FIELDS} } }`;
  const data = await shopifyFetch(query, { id: cartId });
  if (!data.cart) {
    localStorage.removeItem(SHOPIFY_CART_ID_KEY);
    return null;
  }
  return normalizeCart(data.cart);
}

async function shopifyUpdateLine(lineId, quantity) {
  const cartId = await getCartId();
  const query = `
    mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `;
  const data = await shopifyFetch(query, { cartId, lines: [{ id: lineId, quantity }] });
  return normalizeCart(data.cartLinesUpdate.cart);
}

async function shopifyRemoveLine(lineId) {
  const cartId = await getCartId();
  const query = `
    mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `;
  const data = await shopifyFetch(query, { cartId, lineIds: [lineId] });
  return normalizeCart(data.cartLinesRemove.cart);
}

async function shopifyCartCount() {
  try {
    const cart = await shopifyGetCart();
    return cart ? cart.totalQuantity : 0;
  } catch (e) {
    return 0;
  }
}
